import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "../../../../components/ui/Button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { classNames } from "primereact/utils";
import { ColorPicker } from "primereact/colorpicker";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { setSelectedCampaign, setIsEditingCampaign } from "../../../../redux/Slices/adMarketplaceSlice";
import { useCreateAdCampaign, useUpdateAdCampaign, useGetAgencies } from "../../../../queries/adMarketplace";

const buildAdTypes = (t) => [
    { label: t("ad_marketplace.ad_type_video"),         value: "video"         },
    { label: t("ad_marketplace.ad_type_image"),         value: "image"         },
    { label: t("ad_marketplace.ad_type_ticker"),        value: "ticker"        },
    { label: t("ad_marketplace.ad_type_silent_visual"), value: "silent_visual" },
];

export default function AdCampaignDialog() {
    const { t }      = useTranslation();
    const dispatch   = useDispatch();
    const openDialog = useSelector((s) => s.global.openDialog);
    const { selectedCampaign, isEditingCampaign } = useSelector((s) => s.adMarketplace);

    const AD_TYPES = buildAdTypes(t);

    const { data: agenciesData } = useGetAgencies();
    const agencies = (Array.isArray(agenciesData) ? agenciesData : agenciesData?.data ?? [])
        .map((a) => ({ label: a.name, value: a.id }));

    const createMutation = useCreateAdCampaign();
    const updateMutation = useUpdateAdCampaign();

    const existing = isEditingCampaign && selectedCampaign;
    const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
        defaultValues: existing ? {
            agency_id:             selectedCampaign.agency_id,
            name:                  selectedCampaign.name,
            description:           selectedCampaign.description ?? "",
            ad_type:               selectedCampaign.ad_type,
            asset_url:             selectedCampaign.asset_data?.url ?? "",
            ticker_text:           selectedCampaign.asset_data?.text ?? "",
            ticker_color:          selectedCampaign.asset_data?.color ?? "#ffffff",
            ticker_bg_color:       selectedCampaign.asset_data?.bg_color ?? "#000000",
            ticker_speed:          selectedCampaign.asset_data?.speed ?? 5,
            display_duration:      selectedCampaign.asset_data?.display_duration_seconds ?? 15,
            silent_media_type:     selectedCampaign.asset_data?.media_type ?? "image",
            budget:                parseFloat(selectedCampaign.budget) || 0,
            cpm:                   parseFloat(selectedCampaign.cpm) || 0,
            points_per_impression: selectedCampaign.points_per_impression ?? 10,
            start_date:            selectedCampaign.start_date ? new Date(selectedCampaign.start_date) : null,
            end_date:              selectedCampaign.end_date   ? new Date(selectedCampaign.end_date)   : null,
        } : {
            agency_id: null, name: "", description: "", ad_type: null,
            asset_url: "", ticker_text: "", ticker_color: "ffffff", ticker_bg_color: "000000",
            ticker_speed: 5, display_duration: 15, silent_media_type: "image",
            budget: 0, cpm: 0, points_per_impression: 10, start_date: null, end_date: null,
        },
    });

    const adType = watch("ad_type");

    const onClose = () => {
        reset();
        dispatch(setIsEditingCampaign(false));
        dispatch(setSelectedCampaign(null));
        dispatch(toggleDialog("AdCampaignDialog"));
    };

    const buildAssetData = (values) => {
        if (values.ad_type === "video")
            return { url: values.asset_url, thumbnail_url: null, duration_seconds: null };
        if (values.ad_type === "image")
            return { url: values.asset_url, display_duration_seconds: values.display_duration };
        if (values.ad_type === "ticker")
            return {
                text:      values.ticker_text,
                color:     "#" + values.ticker_color,
                bg_color:  "#" + values.ticker_bg_color,
                speed:     values.ticker_speed,
                direction: "left",
            };
        if (values.ad_type === "silent_visual")
            return {
                url:                      values.asset_url,
                media_type:               values.silent_media_type,   // "video" | "image"
                display_duration_seconds: values.display_duration,
                // audio_behaviour is always "keep_original" — enforced server-side,
                // stored here for clarity when the device reads the asset_data
                audio_behaviour:          "keep_original",
            };
        return {};
    };

    const onSubmit = (values) => {
        const payload = {
            agency_id:             values.agency_id,
            name:                  values.name,
            description:           values.description,
            ad_type:               values.ad_type,
            asset_data:            buildAssetData(values),
            budget:                values.budget,
            cpm:                   values.cpm,
            points_per_impression: values.points_per_impression,
            start_date:            values.start_date?.toISOString().split("T")[0],
            end_date:              values.end_date?.toISOString().split("T")[0],
        };

        if (isEditingCampaign) {
            updateMutation.mutate({ id: selectedCampaign.id, ...payload }, { onSuccess: onClose });
        } else {
            createMutation.mutate(payload, { onSuccess: onClose });
        }
    };

    const isLoading = createMutation.isLoading || updateMutation.isLoading;

    return (
        <Dialog
            visible={!!openDialog?.AdCampaignDialog}
            onHide={onClose}
            header={isEditingCampaign ? t("ad_marketplace.edit_campaign") : t("ad_marketplace.new_campaign")}
            style={{ width: "560px" }}
            modal
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-2">

                {/* Agency + Name */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_agency")}</label>
                        <Controller name="agency_id" control={control} rules={{ required: true }} render={({ field }) => (
                            <Dropdown {...field} options={agencies} placeholder={t("ad_marketplace.select_agency")}
                                className={classNames("w-full text-sm", { "p-invalid": errors.agency_id })} />
                        )} />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_campaign_name")}</label>
                        <Controller name="name" control={control} rules={{ required: true }} render={({ field }) => (
                            <InputText {...field} className={classNames("w-full text-sm", { "p-invalid": errors.name })} />
                        )} />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t("form.description")}</label>
                    <Controller name="description" control={control} render={({ field }) => (
                        <InputTextarea {...field} rows={2} className="w-full text-sm" autoResize />
                    )} />
                </div>

                {/* Ad Type */}
                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_ad_type")}</label>
                    <Controller name="ad_type" control={control} rules={{ required: true }} render={({ field }) => (
                        <Dropdown {...field} options={AD_TYPES} placeholder={t("ad_marketplace.select_ad_type")}
                            className={classNames("w-full text-sm", { "p-invalid": errors.ad_type })} />
                    )} />
                </div>

                {/* Creative fields — conditional by type */}
                {adType === "video" && (
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_video_url")}</label>
                        <Controller name="asset_url" control={control} rules={{ required: true }} render={({ field }) => (
                            <InputText {...field} className="w-full text-sm font-mono" placeholder="https://…/ad.mp4" />
                        )} />
                    </div>
                )}

                {adType === "image" && (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_image_url")}</label>
                            <Controller name="asset_url" control={control} rules={{ required: true }} render={({ field }) => (
                                <InputText {...field} className="w-full text-sm font-mono" placeholder="https://…/banner.jpg" />
                            )} />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_display_duration_s")}</label>
                            <Controller name="display_duration" control={control} render={({ field }) => (
                                <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value)}
                                    min={5} max={120} showButtons className="w-full" />
                            )} />
                        </div>
                    </div>
                )}

                {adType === "ticker" && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_ticker_text")}</label>
                            <Controller name="ticker_text" control={control} rules={{ required: true }} render={({ field }) => (
                                <InputText {...field} className="w-full text-sm" placeholder={t("ad_marketplace.ticker_text_ph")} />
                            )} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_text_color")}</label>
                                <Controller name="ticker_color" control={control} render={({ field }) => (
                                    <ColorPicker value={field.value} onChange={(e) => field.onChange(e.value)} format="hex" />
                                )} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_background")}</label>
                                <Controller name="ticker_bg_color" control={control} render={({ field }) => (
                                    <ColorPicker value={field.value} onChange={(e) => field.onChange(e.value)} format="hex" />
                                )} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_speed")}</label>
                                <Controller name="ticker_speed" control={control} render={({ field }) => (
                                    <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value)}
                                        min={1} max={10} showButtons className="w-full" />
                                )} />
                            </div>
                        </div>
                    </div>
                )}

                {adType === "silent_visual" && (
                    <div className="space-y-3">
                        {/* Informational banner — key differentiator */}
                        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                            <i className="pi pi-volume-off text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-blue-700 leading-relaxed">
                                {t("ad_marketplace.silent_visual_info")}
                            </p>
                        </div>

                        {/* Media type toggle */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_asset_type")}</label>
                            <Controller name="silent_media_type" control={control} render={({ field }) => (
                                <div className="flex gap-2">
                                    {[{ label: t("ad_marketplace.asset_image"), value: "image" }, { label: t("ad_marketplace.asset_video_muted"), value: "video" }].map((opt) => (
                                        <button key={opt.value} type="button"
                                            onClick={() => field.onChange(opt.value)}
                                            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                field.value === opt.value
                                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                                            }`}>
                                            <i className={`${opt.value === "image" ? "pi pi-image" : "pi pi-video"} mr-1`} />
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )} />
                        </div>

                        {/* URL */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_asset_url")}</label>
                            <Controller name="asset_url" control={control} rules={{ required: true }} render={({ field }) => (
                                <InputText {...field} className="w-full text-sm font-mono"
                                    placeholder={watch("silent_media_type") === "video" ? "https://…/ad.mp4" : "https://…/banner.jpg"} />
                            )} />
                        </div>

                        {/* Display duration */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_display_duration_sec")}</label>
                            <Controller name="display_duration" control={control} render={({ field }) => (
                                <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value)}
                                    min={5} max={120} showButtons className="w-full" />
                            )} />
                        </div>
                    </div>
                )}

                {/* Budget + CPM + Points */}
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_budget")}</label>
                        <Controller name="budget" control={control} render={({ field }) => (
                            <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value)}
                                mode="currency" currency="USD" locale="en-US" minFractionDigits={2} className="w-full" />
                        )} />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_cpm")}</label>
                        <Controller name="cpm" control={control} render={({ field }) => (
                            <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value)}
                                mode="currency" currency="USD" locale="en-US" minFractionDigits={4} className="w-full" />
                        )} />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_points_per_impression")}</label>
                        <Controller name="points_per_impression" control={control} render={({ field }) => (
                            <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value)}
                                min={0} max={1000} showButtons className="w-full" />
                        )} />
                    </div>
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_start_date")}</label>
                        <Controller name="start_date" control={control} rules={{ required: true }} render={({ field }) => (
                            <Calendar value={field.value} onChange={(e) => field.onChange(e.value)}
                                dateFormat="yy-mm-dd" showIcon
                                className={classNames("w-full", { "p-invalid": errors.start_date })} />
                        )} />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_end_date")}</label>
                        <Controller name="end_date" control={control} rules={{ required: true }} render={({ field }) => (
                            <Calendar value={field.value} onChange={(e) => field.onChange(e.value)}
                                dateFormat="yy-mm-dd" showIcon
                                className={classNames("w-full", { "p-invalid": errors.end_date })} />
                        )} />
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" label={t("buttons.cancel")} className="p-button-text p-button-sm" onClick={onClose} />
                    <Button type="submit" label={isEditingCampaign ? t("buttons.save") : t("ad_marketplace.create_campaign")}
                        icon="pi pi-check" className="p-button-primary p-button-sm" loading={isLoading} />
                </div>
            </form>
        </Dialog>
    );
}
